// Shared Norwegian survey definitions (door-to-door pilot, Asker).
// Stored values are stable enums; labels are for display only.

export const SURVEYORS = [
  { v: "jakob", l: "Jakob" },
  { v: "fredrik", l: "Fredrik" },
];

export const SECTORS = [
  { v: "sector_1", l: "Sektor 1" },
  { v: "sector_2", l: "Sektor 2" },
  { v: "sector_3", l: "Sektor 3" },
  { v: "sector_4", l: "Sektor 4" },
  { v: "other", l: "Annet / ukjent" },
];

export const QUESTIONS = [
  {
    id: "parking_type",
    q: "Har du en oppkjørsel eller privat parkeringsplass nær Asker stasjon?",
    short: "Parkeringstype",
    options: [
      { v: "driveway", l: "Ja – oppkjørsel" },
      { v: "garage", l: "Ja – garasjeplass" },
      { v: "other", l: "Ja – annen type plass" },
      { v: "none", l: "Nei" },
    ],
  },
  {
    id: "walking_distance",
    q: "Omtrent hvor langt er det å gå fra parkeringsplassen din til stasjonen?",
    short: "Gangavstand",
    options: [
      { v: "under_2", l: "Under 2 minutter" },
      { v: "2_5", l: "2–5 minutter" },
      { v: "5_10", l: "5–10 minutter" },
      { v: "over_10", l: "Over 10 minutter" },
    ],
  },
  {
    id: "weekday_availability",
    q: "Står plassen tom på hverdager mens du er på jobb?",
    short: "Ledig hverdager",
    options: [
      { v: "almost_always", l: "Ja, nesten alltid" },
      { v: "some_days", l: "Ja, noen dager" },
      { v: "rarely_never", l: "Sjelden / aldri" },
    ],
  },
  {
    id: "pendli_interest",
    q: "Hvis du kunne tjene penger på plassen ved å registrere den i Pendli – slik at pendlere kan leie den mens du er på jobb – ville du prøvd det?",
    short: "Pendli-interesse",
    options: [
      { v: "yes", l: "Ja, gjerne" },
      { v: "yes_if_easy", l: "Ja, hvis det er enkelt nok å sette opp" },
      { v: "unsure", l: "Usikker – jeg vil vite mer først" },
      { v: "no", l: "Nei" },
    ],
  },
  {
    id: "main_barrier",
    q: "Hva ville vært det største hinderet?",
    short: "Største hinder",
    options: [
      { v: "who_parks", l: "Usikkerhet rundt hvem som parkerer der" },
      { v: "property_damage", l: "Bekymring for skade på eiendom" },
      { v: "too_much_admin", l: "For mye administrasjon" },
      { v: "no_barrier", l: "Jeg ser ikke noe hinder – det høres greit ut" },
      { v: "other", l: "Annet" },
    ],
  },
  {
    id: "acceptable_monthly_income",
    q: "Hva ville vært akseptabel månedsinntekt?",
    short: "Inntektsforventning",
    options: [
      { v: "under_500", l: "Under 500 kr/mnd" },
      { v: "500_1000", l: "500–1 000 kr/mnd" },
      { v: "1000_2500", l: "1 000–2 500 kr/mnd" },
      { v: "over_2500", l: "Over 2 500 kr/mnd" },
    ],
  },
  {
    id: "parking_decision_maker",
    q: "Hvem bestemmer over parkeringsplassen din?",
    short: "Beslutningstaker",
    options: [
      { v: "me_alone", l: "Jeg alene" },
      { v: "me_and_partner", l: "Jeg og samboer/ektefelle" },
      { v: "sameie_borettslag", l: "Sameie eller borettslag – jeg kan ikke leie ut selv" },
      { v: "dont_know", l: "Vet ikke" },
    ],
  },
  {
    id: "contact_permission",
    q: "Kan Pendli varsle deg når appen lanserer?",
    short: "Kontaktsamtykke",
    options: [
      { v: "yes", l: "Ja" },
      { v: "no", l: "Nei" },
    ],
  },
];

export const LABELS = QUESTIONS.reduce(
  (acc, qq) => {
    acc[qq.id] = qq.options.reduce((m, o) => ({ ...m, [o.v]: o.l }), {});
    return acc;
  },
  {
    surveyor: SURVEYORS.reduce((m, o) => ({ ...m, [o.v]: o.l }), {}),
    sector: SECTORS.reduce((m, o) => ({ ...m, [o.v]: o.l }), {}),
  }
);

// Shorter labels for charts / table cells
export const SHORT_LABELS = {
  parking_type: { driveway: "Oppkjørsel", garage: "Garasjeplass", other: "Annen plass", none: "Ingen plass" },
  walking_distance: { under_2: "< 2 min", "2_5": "2–5 min", "5_10": "5–10 min", over_10: "> 10 min" },
  weekday_availability: { almost_always: "Nesten alltid", some_days: "Noen dager", rarely_never: "Sjelden / aldri" },
  pendli_interest: { yes: "Ja, gjerne", yes_if_easy: "Ja, hvis enkelt", unsure: "Usikker", no: "Nei" },
  main_barrier: {
    who_parks: "Hvem parkerer",
    property_damage: "Skade på eiendom",
    too_much_admin: "For mye admin",
    no_barrier: "Ingen hinder",
    other: "Annet",
  },
  acceptable_monthly_income: {
    under_500: "< 500 kr",
    "500_1000": "500–1 000 kr",
    "1000_2500": "1 000–2 500 kr",
    over_2500: "> 2 500 kr",
  },
  parking_decision_maker: {
    me_alone: "Jeg alene",
    me_and_partner: "Jeg + partner",
    sameie_borettslag: "Sameie/borettslag",
    dont_know: "Vet ikke",
  },
  contact_permission: { yes: "Ja", no: "Nei" },
  sector: { sector_1: "1", sector_2: "2", sector_3: "3", sector_4: "4", other: "Ukjent" },
  surveyor: { jakob: "Jakob", fredrik: "Fredrik" },
};

export const FIELD_TITLES = {
  parking_type: "Parkeringstype",
  walking_distance: "Gangavstand til stasjonen",
  weekday_availability: "Ledig på hverdager",
  pendli_interest: "Interesse for Pendli",
  main_barrier: "Største hinder",
  acceptable_monthly_income: "Akseptabel månedsinntekt",
  parking_decision_maker: "Hvem bestemmer",
  contact_permission: "Kan kontaktes",
};

export const shortLabel = (field, value) =>
  (SHORT_LABELS[field] && SHORT_LABELS[field][value]) || "—";
